/* ⚠️ GENERATED from app/services/vibeComments.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/vibeComments.ts — comments on a Vibe: the thread, and who may remove
// what.
//
// 🟢 TWO LEVELS, NOT A TREE, AND THAT IS THE ONE REAL DESIGN DECISION HERE.
//    A reply to a reply attaches to the SAME top-level comment and carries the
//    name it was aimed at, so the thread is a comment and a flat run of replies
//    under it. Unbounded nesting is easy to store and unusable to read: six
//    levels deep on a phone is a column one word wide, and nobody can tell which
//    of two adjacent replies answered which. Every product that tried it — and
//    every product that shipped comments after them — landed here.
//
// 🟥 A REPLY'S `parentId` IS ALWAYS A TOP-LEVEL COMMENT. When somebody replies
//    to a reply, the parent recorded is that reply's OWN parent, never the reply
//    itself. Recording the reply would make a third level exist in the data that
//    no renderer draws — the comment would be written, stored, counted, and
//    invisible.
//
// 🟥 AND DELETING A COMMENT DELETES ITS REPLIES. They answer something that is
//    no longer there; leaving them turns a thread into a page of remarks about
//    nothing, and their authors cannot remove them because they own the reply
//    and not the parent.
//
// 🟥 THE PERMISSION RULE IS ENFORCED ONLY BY THE CLIENTS. `firestore.rules`
//    puts `comments` in the list of fields ANY signed-in account may write, so
//    this decides what the UI offers, not what the database allows. That is a
//    reason for the phone and the browser to hold one rule between them rather
//    than two that can drift.
//
// ══ THREE READERS NOW ══════════════════════════════════════════════════════
// 🟢 GENERATED INTO allo-web AND server/functions (tools/gen-twins.mjs). The
//    server writes comments too — /api/vibes/comment runs every new comment
//    through the safety check before it lands and sends the notifications —
//    so the object it builds, the thread order it assumes and the "hidden"
//    vocabulary it stamps have to be this file's, not a third copy.
//
// ══ WHAT IS NEW ════════════════════════════════════════════════════════════
// • THE LINK. A reply now records `replyToId` — the exact comment it answered,
//   which for a reply-to-a-reply is the reply, not the thread's top. The thread
//   still flattens to two levels (see above); the id is what makes the blue
//   "@name" a link that jumps to the words being answered.
// • YOUR OWN COMMENTS FIRST. buildThreads takes `pinUid`: that person's
//   top-level comments lead the list, oldest first among themselves, then
//   everybody else's oldest first. You always find what you said.
// • HIDDEN FOR HARM. The server stamps `safety` on every comment; a harmful one
//   is `hidden`. Readers see a count and a "Show" — never nothing, never the
//   text by surprise. The author always sees their own, labelled.

/** Bump when the safety check changes enough that old verdicts should be redone. */
export const COMMENT_SAFETY_V = 1;
/** Longer than this is a post, not a comment. */
export const COMMENT_MAX_LEN = 1000;
export const SAFETY_LABELS = ['harassment', 'hate', 'sexual', 'violence', 'profanity', 'self_harm', 'spam'];

/* ═══════════════════════════════════════════════════════════════════════════
   AGREEING, AND NOT — AT ANY SIZE
   ═══════════════════════════════════════════════════════════════════════════
   🟥 IT WAS TWO ARRAYS OF UIDS AND NOTHING ELSE. Every 👍 put a uid inside the
      comment (or, on an old comment, inside the POST document's array), so a
      comment people piled onto bricked itself at ~30,000 thumbs (1 MiB), every
      sheet load downloaded every thumber of every comment, and every thumb was
      a write to one hot document.

   🟢 NOW THE SAME SHAPE AS A POST'S LIKES (services/likeLedger.ts):
        likes / dislikes          — a LEDGER: the first COMMENT_REACT_LEDGER_CAP
                                    of each, exactly as before, so an ordinary
                                    comment reads and fills its icon on its own
        likesExtra / dislikesExtra — how many MORE there are, kept as one
                                    document each (posts/{id}/commentReactions/
                                    {comment}__{uid}) and summed by the server
                                    into this counter (buffered, ≤ 1 write per
                                    1.5 s per instance however hot the comment)
      An old comment in the post's array keeps a smaller ledger and its extras
      live on the post under `commentReactExtra.{key}` — see commentsOf.
      The total is ledger + extra; nothing to migrate, and a comment from
      before any of this reads exactly as it always did.

   🟥 TWO EXCLUSIVE ANSWERS, AS EVER. Agreeing with something you just
      disagreed with moves you; it never puts you in both.

   🟢 "DID I?" — the ledger answers for an ordinary comment. Past it, the server
      says (`myReaction`, on every page it sends and every press it answers;
      createCommentMine below for the old array). `myReaction` is the WIRE's,
      per viewer — it is never stored.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Uids of each kind kept inside a comment's own document. 2 × 100 × ~30 B — nothing. */
export const COMMENT_REACT_LEDGER_CAP = 100;
/** …and inside an OLD comment, which shares the post document with everything else. */
export const LEGACY_REACT_LEDGER_CAP = 20;
/** Comments asked about in one "what did I press?" call. */
export const COMMENT_MINE_BATCH = 100;
const ledgerField = kind => kind === 'like' ? 'likes' : 'dislikes';
const extraField = kind => kind === 'like' ? 'likesExtra' : 'dislikesExtra';

/** The uids a comment holds for one answer. */
export function reactLedger(c, kind) {
  const a = c && c[ledgerField(kind)];
  return Array.isArray(a) ? a.map(x => String(x)).filter(x => !!x) : [];
}
/** How many of one answer live outside the ledger. */
export function reactExtra(c, kind) {
  return Math.max(0, Math.floor(Number(c && c[extraField(kind)]) || 0));
}

/**
 * A comment id as a document id / field-path segment. Ids written by the
 * clients are digits and letters already; anything else is hashed, the same
 * way on the phone, the browser and the server.
 */
export function reactKey(id) {
  const s = String(id == null ? '' : id);
  if (/^[A-Za-z0-9_-]{1,100}$/.test(s)) return s;
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return `h${h.toString(16)}_${s.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40)}`;
}

/** What the ledger alone says this viewer pressed. */
export function ledgerReactionOf(c, uid) {
  const me = String(uid || '');
  if (!me || !c) return null;
  if (reactLedger(c, 'like').includes(me)) return 'like';
  if (reactLedger(c, 'dislike').includes(me)) return 'dislike';
  return null;
}

/** What this viewer did to this comment, if anything. */
export function reactionOf(c, uid) {
  const fromLedger = ledgerReactionOf(c, uid);
  if (fromLedger || !uid || !c) return fromLedger;
  return c.myReaction === 'like' || c.myReaction === 'dislike' ? c.myReaction : null;
}

/** How many agreed, and how many did not — the ledger plus everything past it. */
export function reactionCounts(c) {
  return {
    likes: reactLedger(c, 'like').length + reactExtra(c, 'like'),
    dislikes: reactLedger(c, 'dislike').length + reactExtra(c, 'dislike')
  };
}

/** The comment cannot say for itself whether this viewer pressed something. */
export function reactionNeedsMine(c, uid) {
  if (!c || !uid || c._local) return false;
  if (c.myReaction !== undefined) return false;
  if (!reactExtra(c, 'like') && !reactExtra(c, 'dislike')) return false;
  return !ledgerReactionOf(c, uid);
}
/**
 * The comment's thumbs after `uid` presses `kind` — what the server does, so
 * the optimistic row and the confirmed one cannot disagree.
 * 🟢 PRESSING WHAT YOU ALREADY PRESSED TAKES IT BACK.
 * 🟢 A full ledger is not a refusal: the press goes to the extra count.
 */
export function toggleReaction(c, uid, kind, cap) {
  const me = String(uid || '');
  const room = typeof cap === 'number' ? cap : COMMENT_REACT_LEDGER_CAP;
  const likes = reactLedger(c, 'like').filter(x => x !== me);
  const dislikes = reactLedger(c, 'dislike').filter(x => x !== me);
  let likesExtra = reactExtra(c, 'like');
  let dislikesExtra = reactExtra(c, 'dislike');
  if (!me) return {
    likes,
    dislikes,
    likesExtra,
    dislikesExtra,
    myReaction: null
  };
  const was = reactionOf(c, me);
  /* mine was past the ledger: it comes off the extra count */
  if (was && !ledgerReactionOf(c, me)) {
    if (was === 'like') likesExtra = Math.max(0, likesExtra - 1);else dislikesExtra = Math.max(0, dislikesExtra - 1);
  }
  const want = was === kind ? null : kind;
  if (want) {
    const list = want === 'like' ? likes : dislikes;
    if (list.length < room) list.push(me);else if (want === 'like') likesExtra += 1;else dislikesExtra += 1;
  }
  return {
    likes,
    dislikes,
    likesExtra,
    dislikesExtra,
    myReaction: want
  };
}

/* ═══ "WHAT DID I PRESS?" FOR THE OLD ARRAY — ONE ANSWER PER CLIENT ═══════
   A comment past its ledger that came from the server's pages already says
   (`myReaction`). An OLD comment comes from the post document, which cannot
   carry a per-viewer answer — so it is asked for once (batched per post) and
   remembered; a press on this device is remembered too, so a snapshot of the
   post arriving a moment later cannot spring the thumb back. */

/** How long a press on THIS device outranks what the comment itself says. */
export const MY_REACT_FRESH_MS = 8000;
export function createCommentMine(fetcher, opts) {
  const now = opts && opts.now || (() => Date.now());
  const delayMs = opts && typeof opts.delayMs === 'number' ? opts.delayMs : 150;
  const maxKnown = opts && opts.maxKnown || 3000;
  let me = '';
  let version = 0;
  const known = new Map();
  const asking = new Set();
  const queue = new Map();
  const subs = new Set();
  let timer = null;
  const k = (postId, id) => `${String(postId || '')}|${String(id || '')}`;
  const emit = () => {
    version += 1;
    for (const f of Array.from(subs)) {
      try {
        f();
      } catch (e) {/* theirs */}
    }
  };
  const remember = (key, v, pressed) => {
    known.delete(key);
    known.set(key, {
      v,
      at: now(),
      pressed
    });
    while (known.size > maxKnown) {
      const first = known.keys().next().value;
      if (first === undefined) break;
      known.delete(first);
    }
  };
  async function flush() {
    timer = null;
    const batches = Array.from(queue.entries());
    queue.clear();
    let changed = false;
    for (const [postId, set] of batches) {
      const ids = Array.from(set).slice(0, COMMENT_MINE_BATCH);
      for (const id of ids) asking.add(k(postId, id));
      let res = null;
      // eslint-disable-next-line no-await-in-loop
      try {
        res = fetcher ? await fetcher(postId, ids) : null;
      } catch (e) {
        res = null;
      }
      for (const id of ids) asking.delete(k(postId, id));
      if (!res || !res.ok) continue; /* asked again the next time it is drawn */
      const mine = res.mine || {};
      for (const id of ids) {
        if (known.has(k(postId, id))) continue; /* pressed here while we asked */
        const v = mine[id] === 'like' || mine[id] === 'dislike' ? mine[id] : null;
        remember(k(postId, id), v, false);
        changed = true;
      }
    }
    if (changed) emit();
  }
  return {
    setMe(uid) {
      const u = String(uid || '');
      if (u !== me) {
        me = u;
        known.clear();
        queue.clear();
        emit();
      }
    },
    me: () => me,
    /** The comment as this viewer should see it: `myReaction` filled in when known. */
    view(postId, c) {
      const cc = c;
      if (!cc || !cc.id) return c;
      const got = known.get(k(postId, cc.id));
      if (!got) return c;
      /* 🟢 the ledger is the truth for an ordinary comment — what this device
         remembers speaks only past it, or for a moment after a press here
         (before the post's own copy has caught up) */
      const fresh = got.pressed && now() - got.at < MY_REACT_FRESH_MS;
      const past = (reactExtra(cc, 'like') > 0 || reactExtra(cc, 'dislike') > 0) && !ledgerReactionOf(cc, me);
      if (!fresh && !past) return c;
      if (fresh && ledgerReactionOf(cc, me) !== got.v) {
        /* pressed here a moment ago: the ledger copy may still be the old one */
        const likes = reactLedger(cc, 'like').filter(x => x !== me);
        const dislikes = reactLedger(cc, 'dislike').filter(x => x !== me);
        const extra = {};
        if (got.v === 'like') likes.push(me);
        if (got.v === 'dislike') dislikes.push(me);
        /* mine was counted past the ledger: it comes off there, not twice */
        const was = cc.myReaction === 'like' || cc.myReaction === 'dislike' ? cc.myReaction : null;
        if (was) extra[extraField(was)] = Math.max(0, reactExtra(cc, was) - 1);
        return {
          ...cc,
          likes,
          dislikes,
          ...extra,
          myReaction: got.v
        };
      }
      return cc.myReaction === got.v ? c : {
        ...cc,
        myReaction: got.v
      };
    },
    /** Drawn on screen: if the comment cannot answer for itself, ask (batched per post). */
    want(postId, c) {
      const pid = String(postId || '');
      if (!pid || !me || !c || !c.id || !reactionNeedsMine(c, me)) return;
      const key = k(pid, c.id);
      if (known.has(key) || asking.has(key)) return;
      const set = queue.get(pid) || new Set();
      set.add(String(c.id));
      queue.set(pid, set);
      if (!timer) timer = setTimeout(() => {
        flush();
      }, delayMs);
    },
    /** This device pressed (or the server answered). */
    press(postId, commentId, v) {
      remember(k(postId, commentId), v, true);
      emit();
    },
    forget(postId, commentId) {
      if (known.delete(k(postId, commentId))) emit();
    },
    subscribe(fn) {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },
    version: () => version,
    _flush: () => flush()
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   A LINK TO ONE COMMENT
   ═══════════════════════════════════════════════════════════════════════════
   🟢 THE SAME SHAPE THE CLIENTS ALREADY LAND ON. A bell row carries a
      `commentId` and both clients scroll to it and flash it (see linkTargetOf
      and the jump); this is that landing written down as a URL, so a person can
      hand somebody else the exact answer they are talking about rather than
      "the third reply down, I think".
   🟥 THE POST IS IN THE PATH AND THE COMMENT IS IN THE QUERY. A comment does
      not exist without its post — it lives in an array ON the post — so a link
      that named only the comment could never be resolved. */
export const COMMENT_LINK_BASE = 'https://allloapp.com';
export function commentLink(postId, commentId, base) {
  const root = String(base || COMMENT_LINK_BASE).replace(/\/+$/, '');
  const post = encodeURIComponent(String(postId || ''));
  const c = commentId ? `?c=${encodeURIComponent(String(commentId))}` : '';
  return `${root}/p/${post}${c}`;
}

/** The comment a link points at, or null — the reader of the URL above. */
export function commentFromLink(url) {
  const m = String(url || '').match(/\/p\/([^/?#]+)(?:\?[^#]*\bc=([^&#]+))?/);
  if (!m) return null;
  return {
    postId: decodeURIComponent(m[1]),
    commentId: m[2] ? decodeURIComponent(m[2]) : null
  };
}

/** i18n keys for the row's actions. */
export const ACTION_STR = {
  like: 'vibe_cm_like',
  // "Like"
  dislike: 'vibe_cm_dislike',
  // "Dislike"
  copyLink: 'vibe_cm_copy_link',
  // "Copy link"
  linkCopied: 'vibe_cm_link_copied',
  // "Link copied"
  copyText: 'vibe_cm_copy_text',
  // "Copy text"
  textCopied: 'vibe_cm_text_copied' // "Copied"
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE COMMENT YOU JUST WROTE
   ═══════════════════════════════════════════════════════════════════════════
   🟥 IT APPEARS THE MOMENT YOU PRESS SEND, saying "Posting…", and the same row
      turns into the real comment when the server's copy arrives — never a
      second copy, never a gap. The trick is the id: the composer mints it
      (buildComment: the clock), the server KEEPS it (commentRoutes honours a
      client id), so the live document's copy and the local copy are the same
      comment and mergeLocalComments folds them into one row.
   🟢 A WRITE THAT FAILS LEAVES THE ROW, marked failed, with Retry — the words
      are never lost to a bad connection. A retry re-sends the SAME comment, so
      a write that had in fact landed is recognised (same id) and not doubled. */

/** A comment's id, minted by the client's clock — what the server will keep. */
export const CLIENT_ID_RE = /^\d{10,17}$/;
export function isClientCommentId(id) {
  return CLIENT_ID_RE.test(String(id || ''));
}

/** The list with `c` replacing any comment of the same id, or appended. */
export function upsertComment(list, c) {
  const id = String(c && c.id || '');
  if (!id) return [...(list || []), c];
  let seen = false;
  const out = (list || []).map(x => {
    if (String(x && x.id || '') === id) {
      seen = true;
      return c;
    }
    return x;
  });
  return seen ? out : [...out, c];
}

/** One comment per id — the LAST copy wins (the live document's, when both exist). */
export function dedupeComments(list) {
  const byId = new Map();
  const out = [];
  for (const c of list || []) {
    const id = String(c && c.id || '');
    if (!id) {
      out.push(c);
      continue;
    }
    if (byId.has(id)) out[byId.get(id)] = c;else {
      byId.set(id, out.length);
      out.push(c);
    }
  }
  return out;
}

/**
 * What the thread shows: the document's comments, plus the composer's own
 * copies that the document does not have yet (posting, or failed).
 * 🟥 THE DOCUMENT'S COPY WINS. Once the live list carries the id, the local
 *    copy is dropped — that is the moment "Posting…" becomes "just now".
 */
export function mergeLocalComments(server, local) {
  const have = new Set((server || []).map(c => String(c && c.id || '')).filter(Boolean));
  const extra = (local || []).filter(c => c && c.id && !have.has(String(c.id)));
  return dedupeComments([...(server || []), ...extra]);
}

/** After the document caught up, the local copies with nothing left to say. */
export function pruneLocalComments(local, server) {
  const have = new Set((server || []).map(c => String(c && c.id || '')));
  return (local || []).filter(c => c && c.id && !(have.has(String(c.id)) || c._local === 'posted'));
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE REPLIES, FOLDED
   ═══════════════════════════════════════════════════════════════════════════
   🟥 A THREAD WITH TWELVE ANSWERS IS A WALL. Every product that shows comments
      shows the comment and hides the conversation under it behind one line,
      and the reason is the same everywhere: a reader scrolling for the NEXT
      comment should not have to scroll past somebody else's argument.

   🟢 ONE REPLY IS NOT A CONVERSATION, so it is simply shown — hiding a single
      answer behind "View 1 reply" costs a tap to reveal one line, which is
      worse than showing it.

   🟥 …AND SOME REPLIES ARE NEVER HIDDEN: the one you just wrote (it is still
      "Posting…", and a row that vanishes the instant you send it reads as a
      failure), and the one a link or a notification just aimed at. Those are
      `keepIds`, and the count says "View N more replies" instead. */
export const REPLY_PREVIEW_LIMIT = 1;

/* ═══════════════════════════════════════════════════════════════════════════
   THE SHAPE OF A THREAD, AND THE LINES THAT DRAW IT
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THE DATA IS TWO LEVELS; THE CONVERSATION IS NOT. Every reply hangs off the
      top-level comment (see the header — a third level in the data is a comment
      no renderer draws), but `replyToId` records WHICH comment was answered, so
      the SHAPE of the conversation is known even though the storage is flat.
      This is where that shape is worked out: who answered whom, in what order,
      and — because the answer is the same on both clients — which lines to draw.

   🟢 DEPTH-FIRST, OLDEST FIRST. An answer sits directly under what it answered,
      not at the bottom of the thread, so "…and that is why" follows the remark
      it is about. Indentation is capped at MAX_REPLY_DEPTH: past that the
      column would be one word wide, so deeper answers keep the last indent and
      the "@name" prefix carries the rest of the meaning.

   🟥 THE FLAGS DESCRIBE WHAT IS ON SCREEN, NOT WHAT IS STORED. They are worked
      out AFTER the fold, so a line never descends towards a reply that "View 3
      replies" is hiding — a rail pointing at nothing is worse than no rail.

   Each row carries what a renderer needs and nothing more:
     depth        1 = answers the comment, 2 = answers a reply, capped
     rails[]      for each ANCESTOR level, does the thread continue past this
                  row? (a vertical line straight through this row's gutter)
     isLast       last answer to its parent — the elbow ends here, no line below
     hasChildren  something visible hangs off THIS row, so its own rail starts */
export const MAX_REPLY_DEPTH = 3;
/** The in-thread parent of a reply: the reply it answered, or '' for the comment. */
function parentInThread(r, byId) {
  const want = r && r.replyToId ? String(r.replyToId) : '';
  return want && byId.has(want) && want !== String(r.id) ? want : '';
}

/**
 * The replies, depth-first: each answer directly under what it answered.
 * 🟢 A reply whose target is gone (deleted, or written before `replyToId`
 *    existed) answers the comment itself — which is where it visibly belongs.
 */
export function orderReplies(replies) {
  const all = (replies || []).filter(r => r && r.id);
  const byId = new Map(all.map(r => [String(r.id), r]));
  const kids = new Map();
  for (const r of all) {
    const p = parentInThread(r, byId);
    const arr = kids.get(p) || [];
    arr.push(r);
    kids.set(p, arr);
  }
  for (const arr of kids.values()) arr.sort((a, b) => commentMs(a.timestamp) - commentMs(b.timestamp));
  const out = [];
  const seen = new Set();
  const walk = parent => {
    for (const r of kids.get(parent) || []) {
      const id = String(r.id);
      /* 🟥 A CYCLE IS ONE BAD `replyToId` AWAY from an infinite walk, and the
         ids are minted by a clock on a device whose clock can be wrong. */
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({
        comment: r,
        parentId: parent
      });
      walk(id);
    }
  };
  walk('');
  /* anything a cycle stranded is still somebody's words: put it under the comment */
  for (const r of all) if (!seen.has(String(r.id))) out.push({
    comment: r,
    parentId: ''
  });
  return out;
}

/** The tree flags for exactly the rows that will be drawn. */
function flagRows(rows) {
  const present = new Set(rows.map(r => String(r.comment.id)));
  /* a kept row whose parent is not kept hangs off the comment instead */
  const parentOf = new Map();
  for (const r of rows) parentOf.set(String(r.comment.id), present.has(r.parentId) ? r.parentId : '');
  const depthOf = new Map();
  const depth = id => {
    if (depthOf.has(id)) return depthOf.get(id);
    const p = parentOf.get(id) || '';
    const d = Math.min(MAX_REPLY_DEPTH, p ? depth(p) + 1 : 1);
    depthOf.set(id, d);
    return d;
  };
  const idx = new Map();
  rows.forEach((r, i) => idx.set(String(r.comment.id), i));
  const laterSibling = id => {
    const p = parentOf.get(id) || '';
    const i = idx.get(id);
    return rows.some((r, j) => j > i && (parentOf.get(String(r.comment.id)) || '') === p);
  };
  return rows.map(r => {
    const id = String(r.comment.id);
    const d = depth(id);
    /* the ancestors, nearest first, then reversed into drawing order */
    const rails = [];
    let up = parentOf.get(id) || '';
    while (up) {
      rails.push(laterSibling(up));
      up = parentOf.get(up) || '';
    }
    rails.reverse();
    return {
      comment: r.comment,
      depth: d,
      rails: rails.slice(0, Math.max(0, d - 1)),
      isLast: !laterSibling(id),
      hasChildren: rows.some(x => (parentOf.get(String(x.comment.id)) || '') === id)
    };
  });
}
export function replyView(replies, opts) {
  const ordered = orderReplies(replies);
  const expanded = !!(opts && opts.expanded);
  const show = (rows, hidden, canExpand) => {
    const shown = flagRows(rows);
    return {
      shown,
      hiddenCount: hidden,
      canExpand,
      someShown: shown.length > 0,
      rootHasChildren: shown.some(r => r.depth === 1)
    };
  };
  if (ordered.length <= REPLY_PREVIEW_LIMIT) return show(ordered, 0, false);
  if (expanded) return show(ordered, 0, true);
  const raw = opts && opts.keepIds || null;
  const keep = raw instanceof Set ? new Set([...raw].map(x => String(x))) : new Set((raw || []).map(x => String(x)));
  if (!keep.size) return show([], ordered.length, true);
  /* 🟢 A KEPT REPLY BRINGS ITS ANCESTORS WITH IT. "Posting…" three levels down
     with nothing above it is a remark with no subject; the chain it answers is
     what makes it readable. */
  const byRow = new Map(ordered.map(r => [String(r.comment.id), r]));
  const want = new Set();
  for (const id of keep) {
    let cur = id;
    let guard = 0;
    while (cur && byRow.has(cur) && !want.has(cur) && guard < 50) {
      want.add(cur);
      cur = byRow.get(cur).parentId;
      guard += 1;
    }
  }
  const rows = ordered.filter(r => want.has(String(r.comment.id)));
  const hidden = ordered.length - rows.length;
  /* 🟥 A TOGGLE WITH NOTHING BEHIND IT IS NOT A TOGGLE. When what must stay
     visible (a reply being sent, the one a link aimed at) plus its ancestors
     happens to be ALL of them, nothing is folded — and this used to still say
     it could expand, so the thread ended with "View 0 more replies". The fold
     exists because something is hidden; if nothing is, there is no fold. */
  return show(rows, hidden, hidden > 0);
}

/** Which comments must stay visible whatever the fold says. */
export function keepVisibleIds(replies, opts) {
  const out = [];
  const focus = opts && opts.focusId ? String(opts.focusId) : '';
  for (const r of replies || []) {
    if (!r || !r.id) continue;
    /* still on its way, or it did not send — never hide what the composer just did */
    if (r._local === 'posting' || r._local === 'failed') {
      out.push(String(r.id));
      continue;
    }
    if (focus && String(r.id) === focus) out.push(String(r.id));
  }
  return out;
}

/** What the toggle says, and with what number. */
/* 🟢 THE GUTTER, IN ONE PLACE. Both clients draw the same tree, so both need
   the same arithmetic for where a rail sits and how far a row is indented —
   and a rail half a pixel off the avatar it descends from is the whole
   difference between "a thread" and "some stray lines". The numbers are in
   logical pixels and the browser uses them as px; the ROW's height is never
   named, because a line that knows a row's height is a line that is wrong the
   moment somebody writes a long reply. See the renderers: they pin the rail to
   top and bottom and let it stretch. */
export const THREAD_GEOM = {
  /** the top-level comment's avatar */
  rootAvatar: 35,
  rootLeft: 0,
  /** a reply's avatar */
  replyAvatar: 28,
  /** where a depth-1 reply's own column starts */
  firstIndent: 46,
  /** each further level */
  step: 26,
  /** the row's own top padding — where an avatar begins */
  padTop: 10
};

/** Where the column of a reply at this depth starts. */
export function threadIndent(depth) {
  const d = Math.max(1, Math.min(MAX_REPLY_DEPTH, depth || 1));
  return THREAD_GEOM.firstIndent + (d - 1) * THREAD_GEOM.step;
}

/** The x of the vertical line that descends FROM a comment at this depth (0 = the comment). */
export function threadRailX(depth) {
  if (depth <= 0) return THREAD_GEOM.rootLeft + THREAD_GEOM.rootAvatar / 2;
  return threadIndent(depth) + THREAD_GEOM.replyAvatar / 2;
}

/** How far down a row its avatar's middle sits — where an elbow turns in. */
export function threadElbowY(depth) {
  return THREAD_GEOM.padTop + (depth <= 0 ? THREAD_GEOM.rootAvatar : THREAD_GEOM.replyAvatar) / 2;
}

/** Where a row's OWN rail begins: just under its avatar. */
export function threadRailStartY(depth) {
  return THREAD_GEOM.padTop + (depth <= 0 ? THREAD_GEOM.rootAvatar : THREAD_GEOM.replyAvatar);
}

/** The horizontal reach of the elbow that turns into a row at this depth. */
export function threadElbowW(depth) {
  return Math.max(8, threadIndent(depth) - threadRailX(depth - 1) - 6);
}
export function replyToggleLabel(v, expanded) {
  if (!v || !v.canExpand) return null;
  if (expanded) return {
    key: REPLY_STR.hide,
    count: 0
  };
  /* 🟢 "View 1 reply" whether or not one is already showing — "View 1 more
     replies" is what you get from doing this the obvious way round. */
  if (v.hiddenCount === 1) return {
    key: REPLY_STR.viewOne,
    count: 1
  };
  if (v.someShown) return {
    key: REPLY_STR.viewMore,
    count: v.hiddenCount
  };
  return {
    key: REPLY_STR.view,
    count: v.hiddenCount
  };
}
export const REPLY_STR = {
  view: 'vibe_cm_view_replies',
  // "View {{count}} replies"
  viewOne: 'vibe_cm_view_reply',
  // "View 1 reply"
  viewMore: 'vibe_cm_view_more',
  // "View {{count}} more replies"
  hide: 'vibe_cm_hide_replies' // "Hide replies"
};

/** i18n keys for the row's own state — the phone's locales and the browser's table. */
export const LOCAL_STR = {
  posting: 'vibe_cm_posting',
  // "Posting…"
  failed: 'vibe_cm_failed',
  // "Failed to post"
  retry: 'vibe_cm_retry',
  // "Retry"
  justNow: 'vibe_cm_just_now' // "just now"
};

/**
 * How old a comment is, as a key and a number — "just now" under a minute,
 * then minutes, hours, days; a week or more is a date the client formats.
 */
export function commentAge(ts, now) {
  const ms = commentMs(ts);
  const diff = Math.max(0, (now || Date.now()) - ms);
  if (!ms) return {
    unit: 'now',
    n: 0,
    ms: 0
  };
  if (diff < 60 * 1000) return {
    unit: 'now',
    n: 0,
    ms
  };
  if (diff < 60 * 60 * 1000) return {
    unit: 'm',
    n: Math.floor(diff / 60000),
    ms
  };
  if (diff < 24 * 60 * 60 * 1000) return {
    unit: 'h',
    n: Math.floor(diff / 3600000),
    ms
  };
  if (diff < 7 * 24 * 60 * 60 * 1000) return {
    unit: 'd',
    n: Math.floor(diff / 86400000),
    ms
  };
  return {
    unit: 'date',
    n: 0,
    ms
  };
}

/** How deep a reply may be aimed. Kept as a name so the reason has somewhere to live. */
export const MAX_THREAD_DEPTH = 2;

/**
 * The comments on a post, cleaned of the entries that would crash a renderer.
 *
 * 🟥 A COMMENT WITH NO id IS UNDELETABLE AND UNTHREADABLE. Old rows predate ids;
 *    they are kept and shown — they are somebody's words — but nothing can point
 *    at them, so they can never be a parent and can never be removed by id.
 */
export function commentsOf(post) {
  const list = Array.isArray(post?.comments) ? post.comments : [];
  const ok = list.filter(c => c && typeof c === 'object' && typeof c.content === 'string');
  /* 👍👎 an OLD comment's thumbs past its ledger are counted on the post
     (commentReactExtra.{key}) — laid over it here, so every reader sees the total */
  const extra = post && post.commentReactExtra && typeof post.commentReactExtra === 'object' ? post.commentReactExtra : null;
  if (!extra) return ok;
  return ok.map(c => {
    const x = c.id != null ? extra[reactKey(c.id)] : null;
    if (!x || typeof x !== 'object') return c;
    return {
      ...c,
      likesExtra: reactExtra(x, 'like'),
      dislikesExtra: reactExtra(x, 'dislike')
    };
  });
}

/** An old comment as the post's array stores it — the overlay and the wire's fields left out. */
export function storedLegacyComment(c) {
  const out = {
    ...(c || {})
  };
  delete out.likesExtra;
  delete out.dislikesExtra;
  delete out.myReaction;
  delete out._local;
  return out;
}

/** Milliseconds out of whatever shape a writer left behind. */
export function commentMs(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  const p = Date.parse(String(v));
  return Number.isFinite(p) ? p : 0;
}
/**
 * The flat array, arranged into threads.
 *
 * 🟢 TOP-LEVEL COMMENTS OLDEST FIRST, AND REPLIES OLDEST FIRST UNDER THEM —
 *    a conversation read top to bottom. The array's own order is not trusted:
 *    the edit path removes and re-appends, which moves an edited comment to the
 *    end, and the admin delete rewrites the array wholesale.
 *
 * 🟥 A REPLY WHOSE PARENT IS GONE IS PROMOTED, NOT DROPPED. Parents disappear —
 *    an older delete that did not sweep replies, a comment removed by a
 *    moderator. Dropping the orphan hides somebody's words with no trace;
 *    showing it at the top level keeps them readable and deletable.
 */
/* ═══════════════════════════════════════════════════════════════════════════
   THE ORDER OF A CONVERSATION
   ═══════════════════════════════════════════════════════════════════════════
   🟢 TWO ORDERS, AND ONLY TWO. "Top" is what most people agreed with; "Newest"
      is what was just said. A third — "with media" — is what every other app
      offers and this one cannot: a comment here is text, so the filter would
      always come back empty. An order that can only ever say "nothing" is a
      promise the product does not keep, so it is not offered.

   🟥 AND ONLY THE TOP LEVEL IS RE-ORDERED. A thread is a conversation: the
      reply that answers a reply has to come after the thing it answers, or the
      "@name" it opens with points at something below it on screen. Sorting the
      replies by score would shuffle an argument into nonsense. Replies stay
      oldest-first under every sort, always.

   🟥 AND THE PIN SURVIVES BOTH. "My own comments first" is not a sort order —
      it is how a person finds what they said — so it is applied before either.
   ═══════════════════════════════════════════════════════════════════════════ */
export const COMMENT_SORTS = ['top', 'newest'];
export const DEFAULT_COMMENT_SORT = 'top';

/** i18n keys for the header and its little menu. */
export const SORT_STR = {
  count: 'vibe_ui_comments_count',
  // "33 comments"
  title: 'vibe_cm_sort',
  // "Sort comments"
  top: 'vibe_cm_sort_top',
  // "Top"
  newest: 'vibe_cm_sort_newest' // "Newest"
};

/** The one a menu should tick, given whatever is stored. */
export function commentSortOf(v) {
  const s = String(v || '');
  return COMMENT_SORTS.includes(s) ? s : DEFAULT_COMMENT_SORT;
}

/**
 * How much a comment was agreed with.
 * 🟢 AGREEMENT MINUS DISAGREEMENT, not likes alone: a remark twenty people
 *    liked and forty disliked is not the top comment, and ranking it there is
 *    how a thread ends up led by whatever made people angriest.
 */
export function commentScore(c) {
  /* every thumb, not just the ledger's — a comment 50,000 people agreed with
     outranks one that 100 did */
  const rc = reactionCounts(c);
  return rc.likes - rc.dislikes;
}
export function buildThreads(comments, opts) {
  const list = comments || [];
  const pin = opts && opts.pinUid ? String(opts.pinUid) : '';
  /* 📌 the comment the post's owner pinned — first of all, before even your own */
  const pinnedId = opts && opts.pinnedId ? String(opts.pinnedId) : '';
  const byId = new Map();
  for (const c of list) if (c.id) byId.set(String(c.id), c);
  const tops = [];
  const repliesFor = new Map();
  for (const c of list) {
    const parent = c.parentId ? String(c.parentId) : '';
    if (parent && byId.has(parent) && parent !== String(c.id)) {
      const arr = repliesFor.get(parent) || [];
      arr.push(c);
      repliesFor.set(parent, arr);
    } else {
      tops.push(c);
    }
  }
  const byTime = (a, b) => commentMs(a.timestamp) - commentMs(b.timestamp);
  const sort = commentSortOf(opts && opts.sort);
  /* 🟢 YOURS FIRST, THEN THE ORDER THE READER ASKED FOR. The pin is applied
     before the sort, so a person always finds what they said whichever order
     the thread is in; within each group the chosen order decides.
       · top     — most agreed with, newest breaking a tie
       · newest  — what was just said
     Replies are never pinned and never re-ordered — see the note above. */
  const byOrder = sort === 'newest' ? (a, b) => byTime(b, a)
  /* 🟥 A TIE IS BROKEN BY THE OLDER ONE, NOT THE NEWER. In a thread where
     nobody has voted yet — which is every thread for its first hour, and most
     threads forever — every score is 0, so the tie-break IS the order. Ranking
     ties newest-first would silently turn every quiet conversation upside
     down and call it "Top". Oldest-first leaves an unvoted thread reading the
     way it was written, and "Top" only rearranges it once people actually
     say what they think of it. */ : (a, b) => commentScore(b) - commentScore(a) || byTime(a, b);
  const lead = c => pinnedId && String(c.id) === pinnedId ? 0 : pin && String(c.userId) === pin ? 1 : 2;
  tops.sort((a, b) => {
    const am = lead(a);
    const bm = lead(b);
    return am !== bm ? am - bm : byOrder(a, b);
  });
  return tops.map(c => ({
    comment: c,
    replies: (repliesFor.get(String(c.id)) || []).slice().sort(byTime)
  }));
}

/** How many comments a post has, replies included — one number, one definition. */
export function commentCountOf(post) {
  const list = commentsOf(post);
  /* 💬 A PAGED POST'S ARRAY IS ONLY ITS OLD COMMENTS. Once the server writes
     comments as their own documents (services/commentPages), `commentsCount`
     is the server's own running total — legacy array included — and the array
     is a fraction of it. The larger of the two survives a stale counter. */
  if (post && post.commentsV2 === true) return Math.max(list.length, Math.max(0, Number(post.commentsCount) || 0));
  if (list.length) return list.length;
  return Number(post?.commentsCount) || 0;
}

/**
 * The count to store after the ARRAY was rewritten from `prevLen` to `nextLen`
 * comments. 🟥 On a paged post the array is not the total: writing its length
 * would erase every comment that lives in its own document. Returns the delta
 * as `{ delta }` there, and the plain length otherwise.
 */
export function countAfterArrayChange(post, prevLen, nextLen) {
  if (post && post.commentsV2 === true) return {
    delta: (Number(nextLen) || 0) - (Number(prevLen) || 0)
  };
  return {
    set: Math.max(0, Number(nextLen) || 0)
  };
}

/** The number a screen shows right after a local change of `delta` comments. */
export function localCountAfter(post, list, delta) {
  if (post && post.commentsV2 === true) return Math.max(0, (Number(post.commentsCount) || 0) + (Number(delta) || 0));
  return Array.isArray(list) ? list.length : 0;
}

/**
 * The comment object a composer writes.
 *
 * 🟥 `parentId` IS THE TOP-LEVEL ID, resolved here rather than at each call
 *    site. `replyingTo` may itself be a reply — that is what tapping Reply on a
 *    reply gives you — and storing it verbatim would create the third level
 *    nothing renders.
 */
export function buildComment(opts) {
  const content = String(opts?.text || '').trim();
  if (!content) return null;
  if (content.length > COMMENT_MAX_LEN) return null;
  const r = opts.replyingTo || null;
  const parentId = r ? String(r.parentId || r.id || '') : '';
  return {
    id: String(opts.now || Date.now()),
    userId: opts.me.uid,
    userName: opts.me.name,
    userImage: opts.me.image || '',
    content,
    timestamp: new Date(opts.now || Date.now()),
    ...(parentId ? {
      parentId,
      replyToName: r ? String(r.userName || '') : '',
      /* the link: the exact comment answered, even when the thread flattens it */
      replyToId: r && r.id ? String(r.id) : null,
      replyToUid: r && r.userId ? String(r.userId) : null
    } : {})
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE LINK
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The comment a reply answered, if it is still there — `replyToId` first, the
 * thread's top otherwise (replies written before the link existed). Null when
 * it was removed, which the client says out loud rather than jumping nowhere.
 */
export function linkTargetOf(reply, comments) {
  if (!reply) return null;
  const list = comments || [];
  const want = reply.replyToId ? String(reply.replyToId) : '';
  if (want) {
    const hit = list.find(c => String(c.id || '') === want);
    if (hit) return hit;
  }
  const parent = reply.parentId ? String(reply.parentId) : '';
  if (parent) {
    const hit = list.find(c => String(c.id || '') === parent);
    if (hit) return hit;
  }
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   HIDDEN FOR HARM
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THE SERVER DECIDES, THE CLIENTS FOLD. A comment is hidden when the check
      said so (`hidden: true`, `safety.status: 'hidden'`). Neither client runs
      the check — a word list on a phone is a word list somebody can read out of
      the bundle — but both apply the same reading of the stamp, so a comment
      folded on the phone is folded in the browser. */

export function isCommentHidden(c) {
  return !!c && (c.hidden === true || c.safety && c.safety.status === 'hidden');
}

/**
 * The comments a viewer sees, and how many were folded away.
 *
 * 🟢 THE AUTHOR ALWAYS SEES THEIR OWN — labelled, so they know others do not.
 *    Silently vanishing somebody's words is what makes people post them three
 *    more times. Everybody else sees the count and a way to show them.
 * 🟢 A HIDDEN PARENT'S REPLIES STAY. They answer something now folded, so
 *    buildThreads promotes them (its orphan rule); their "@name" still says who
 *    they answered. Innocent replies are not punished for what they answered.
 */
export function partitionComments(comments, opts) {
  const list = comments || [];
  const me = opts && opts.viewerUid ? String(opts.viewerUid) : '';
  const visible = [];
  const hidden = [];
  for (const c of list) {
    if (!isCommentHidden(c)) {
      visible.push(c);
      continue;
    }
    hidden.push(c);
    if (opts && opts.reveal || me && String(c.userId) === me) visible.push(c);
  }
  /* what the "N comments hidden" line counts: the ones this viewer is NOT
     already seeing as their own */
  const hiddenCount = hidden.filter(c => !(me && String(c.userId) === me)).length;
  return {
    visible,
    hidden,
    hiddenCount
  };
}

/** Does this comment still need (or need again) the safety check? */
export function safetyDue(c, now) {
  if (!c || typeof c.content !== 'string') return false;
  const s = c.safety;
  if (!s) return true;
  if (s.locked) return false;
  if (Number(s.v) < COMMENT_SAFETY_V) return true;
  if (s.status === 'clear' || s.status === 'hidden') return false;
  /* pending / error: try again, but not in a tight loop */
  const at = Number(s.at) || 0;
  return (now || Date.now()) - at > 10 * 60 * 1000;
}

/** The threshold the model's confidence has to clear. */
export const SAFETY_MIN_CONFIDENCE = 0.7;

/**
 * The verdict from what the two checks said.
 * 🟥 THE WORD LIST WINS WHEN IT HITS — it is the sure part. The model is asked
 *    for what a list cannot see (a slur spelled sideways, a threat with no bad
 *    word in it, any of the languages the list does not cover); its say-so
 *    hides only above SAFETY_MIN_CONFIDENCE. No model and no hit is `pending`,
 *    visible, and re-checked — never silently "clear".
 */
export function safetyVerdict(input) {
  const at = input.now || Date.now();
  const lx = input.lexicon;
  if (lx && lx.hit) return {
    v: COMMENT_SAFETY_V,
    status: 'hidden',
    at,
    labels: lx.labels && lx.labels.length ? lx.labels : ['profanity'],
    via: 'lexicon'
  };
  const m = input.model;
  if (m) {
    if (m.harmful && Number(m.confidence) >= SAFETY_MIN_CONFIDENCE) {
      return {
        v: COMMENT_SAFETY_V,
        status: 'hidden',
        at,
        labels: m.labels && m.labels.length ? m.labels : ['harassment'],
        via: 'model'
      };
    }
    return {
      v: COMMENT_SAFETY_V,
      status: 'clear',
      at,
      via: 'model'
    };
  }
  if (input.modelUnavailable) return {
    v: COMMENT_SAFETY_V,
    status: 'pending',
    at
  };
  /* a list that did not hit and no model asked: still not a verdict */
  return {
    v: COMMENT_SAFETY_V,
    status: 'pending',
    at
  };
}

/** i18n keys for what the person sees — the phone's locales and the browser's table. */
export const COMMENT_STR = {
  hiddenSome: 'vibe_cm_hidden_some',
  // "{{count}} comments hidden because they may be harmful"
  hiddenOne: 'vibe_cm_hidden_one',
  // "1 comment hidden because it may be harmful"
  show: 'vibe_cm_hidden_show',
  // "Show"
  hide: 'vibe_cm_hidden_hide',
  // "Hide again"
  yoursHidden: 'vibe_cm_yours_hidden',
  // "Only you can see this — it may be hurtful to others"
  hiddenTag: 'vibe_cm_hidden_tag',
  // "Hidden"
  replyGone: 'vibe_cm_reply_gone',
  // "The comment this answered was removed"
  tooLong: 'vibe_cm_too_long' // "Comments can be up to 1,000 characters"
};

/**
 * May `viewerUid` remove this comment?
 *
 * 🟢 THREE PEOPLE CAN: whoever wrote it, whoever owns the post, and — when the
 *    post was published by a page — whoever owns that page. The third is not a
 *    nicety: a page post's `userId` is the PAGE's id, not a person's, so the
 *    "post owner" test alone leaves nobody able to moderate a page's own
 *    comments from inside the app.
 *
 * 🟥 A COMMENT WITH NO id CANNOT BE REMOVED BY ANYONE, whatever the answer here
 *    — nothing can name it. Saying so up front stops a button appearing that
 *    cannot work.
 */
export function canDeleteComment(opts) {
  const {
    comment,
    post,
    viewerUid
  } = opts;
  if (!comment || !comment.id || !viewerUid) return false;
  if (comment.userId === viewerUid) return true;
  return isPostOwner({
    post,
    viewerUid,
    myPageIds: opts.myPageIds
  });
}

/**
 * Is `viewerUid` the post's owner — the person who published it, or the owner of the page that did?
 * One answer for every owner power: removing anyone's comment, commenting on a locked post, pinning.
 */
export function isPostOwner(opts) {
  const {
    post,
    viewerUid
  } = opts;
  if (!post || !viewerUid) return false;
  if (post.userId && String(post.userId) === String(viewerUid)) return true;
  if (post.authorType === 'page' && post.pageId) return (opts.myPageIds || []).map(String).includes(String(post.pageId));
  return false;
}

/**
 * May `viewerUid` write a comment (or a reply) on this post?
 *
 * 🟥 TURNING COMMENTS OFF SILENCED THE OWNER TOO. "Comments off" is the owner closing the floor to
 *    everybody else — not taking their own voice away: they could not answer a question, add a
 *    correction, or reply to what was already there.
 * 🟢 Everyone while comments are on; only the owner (or the page's owner) while they are off. The
 *    server asks the same question before it writes (commentRoutes.addComment).
 */
export function canCommentOn(opts) {
  if (!opts.post || !opts.viewerUid) return false;
  if (opts.post.commentsEnabled !== false) return true;
  return isPostOwner(opts);
}

/* ═══ 📌 THE PINNED COMMENT ═══════════════════════════════════════════════════
   🟢 ONE PER POST, CHOSEN BY ITS OWNER — their own or anybody's. It leads the thread for every reader
      (before even the reader's own), in either order, with a "Pinned" label and its replies under it.
   🟢 A TOP-LEVEL COMMENT ONLY. A reply is pinned by pinning what it answers — a reply lifted out of its
      thread reads as an answer to nothing.
   🟢 STORED ON THE POST (`pinnedCommentId`), written by the server for the owner only; deleting the
      comment un-pins it, and the first page of a paged post always carries it. */
export function pinnedCommentIdOf(post) {
  const id = post && post.pinnedCommentId;
  return id ? String(id) : null;
}

/** May `viewerUid` pin (or un-pin) this comment? The post's owner, a top-level comment that exists. */
export function canPinComment(opts) {
  const c = opts.comment;
  if (!c || !c.id || c.parentId || c._local) return false; /* still on its way, or failed: nothing to pin yet */
  return isPostOwner(opts);
}

/** What pressing Pin on this comment sets the post's pin to: it, or nothing if it was already pinned. */
export function nextPinnedId(post, commentId) {
  const cur = pinnedCommentIdOf(post);
  return cur && cur === String(commentId) ? null : String(commentId);
}

/** i18n keys for the pin and the owner-only composer */
export const PIN_STR = {
  pin: 'vibe_cm_pin',
  // "Pin"
  unpin: 'vibe_cm_unpin',
  // "Unpin"
  pinned: 'vibe_cm_pinned',
  // "Pinned"
  pinFailed: 'vibe_cm_pin_failed',
  // "That comment could not be pinned."
  ownerOnly: 'vibe_cm_owner_only' // "Comments are off — only you can comment"
};

/**
 * Who should hear about a comment that was just written, and as what.
 *
 * 🟥 A REPLY HAS A SECOND AUDIENCE THAT NOTHING WAS TELLING. Answering somebody
 *    notified the POST'S OWNER — who may not be in the conversation at all —
 *    and said nothing to the person actually being answered. On a page's post
 *    that is the common case, not the edge one: the owner is a page, and the two
 *    people talking are both strangers to it.
 *
 * 🟥 AND NOBODY IS TOLD TWICE. When the person you are answering IS the post's
 *    owner, they get one notification, and it is the reply — the more specific
 *    of the two. Two rows in the bell for one comment reads as a bug, and two
 *    pushes for it reads as spam.
 *
 * 🟢 THE ORDER IS DELIBERATE: the reply first, so the dedupe below keeps the
 *    specific one and drops the general one rather than the other way round.
 */

export function recipientsForComment(opts) {
  const author = String(opts.authorUid || '');
  const owner = String(opts.postOwnerUid || '');
  const parent = opts.replyingTo ? String(opts.replyingTo.userId || '') : '';
  const out = [];
  const seen = new Set();
  const add = (uid, kind) => {
    /* 🟥 NEVER YOURSELF. Answering your own comment, or commenting on your own
       post, must be silent — a notification about something you just did is
       noise at best and, on a busy thread of your own, a phone that will not
       stop buzzing. */
    if (!uid || uid === author || seen.has(uid)) return;
    seen.add(uid);
    out.push({
      uid,
      kind
    });
  };
  if (parent) add(parent, 'reply');
  add(owner, 'comment');
  return out;
}

/**
 * Was this comment written by the official account?
 *
 * 🟥 KEYED ON THE PAGE ID, NEVER ON THE NAME. The portal can now answer a
 *    comment as Allo, and an answer from Allo that looks like a comment from
 *    somebody who called themselves Allo is worse than no answer at all — it
 *    teaches people that the name is the credential. `pageId` is set by the
 *    server route and cannot be written by a client: `firestore.rules` lets any
 *    signed-in account append to `comments`, so a name match here would hand the
 *    badge to whoever asked for it.
 */
export function isOfficialComment(c, officialPageId) {
  return !!officialPageId && String(c?.pageId || '') === officialPageId;
}

/** Only the person who wrote it may edit it. */
export function canEditComment(opts) {
  return !!opts.comment?.id && !!opts.viewerUid && opts.comment.userId === opts.viewerUid;
}

/**
 * The comments array with one comment and everything answering it removed.
 *
 * 🟥 BY id, AND AS A REWRITE — never `arrayRemove`. arrayRemove matches on deep
 *    equality of the whole element, and a comment's `timestamp` does not survive
 *    the round trip unchanged: the feed normalises it to a number for rendering,
 *    so removing a comment taken from feed state matched nothing and the write
 *    silently succeeded while the comment stayed. The local list said it worked.
 */
export function withoutComment(comments, commentId) {
  const id = String(commentId || '');
  if (!id) return comments || [];
  return (comments || []).filter(c => String(c.id || '') !== id && String(c.parentId || '') !== id);
}

/** How many rows one delete will take with it — for the confirmation wording. */
export function replyCountOf(comments, commentId) {
  const id = String(commentId || '');
  if (!id) return 0;
  return (comments || []).filter(c => String(c.parentId || '') === id).length;
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE LONG-PRESS PANEL — what one comment offers past Reply
   🟥 EVERY ROW CARRIED EVERY CONTROL: 👍 👎 Reply Pin Edit Delete 🔗 — seven targets on a
      line under every comment, most of them for one person (the author, the post's owner).
   🟢 The row keeps 👍 👎 and Reply; the rest is what a long press opens, from the bottom
      (src/vibes/comments/CommentActions). The same rules decide each item as decided each
      button — this only orders them, and offers nothing a comment still on its way can do.
   ═══════════════════════════════════════════════════════════════════════════ */

/** The panel's items for one comment, in order (copy, then the author's / owner's, Delete last). */
export function commentMenu(opts) {
  const c = opts.comment;
  if (!c || c._local) return []; // still on its way, or failed: the row's own Retry / Delete
  const uid = String(opts.viewerUid || '');
  const gate = {
    comment: c,
    post: opts.post,
    viewerUid: uid,
    myPageIds: opts.myPageIds || []
  };
  const out = [];
  if (typeof c.content === 'string' && c.content.trim()) out.push({
    key: 'copyText',
    label: ACTION_STR.copyText,
    icon: 'copy-outline'
  });
  if (c.id) out.push({
    key: 'copyLink',
    label: ACTION_STR.copyLink,
    icon: 'link-outline'
  });
  if (uid && canPinComment(gate)) {
    const pinned = !opts.isReply && !!c.id && String(c.id) === pinnedCommentIdOf(opts.post);
    out.push(pinned ? {
      key: 'unpin',
      label: PIN_STR.unpin,
      icon: 'pin-outline'
    } : {
      key: 'pin',
      label: PIN_STR.pin,
      icon: 'pin'
    });
  }
  if (uid && canEditComment({
    comment: c,
    viewerUid: uid
  })) out.push({
    key: 'edit',
    label: 'vibe_ui_edit',
    icon: 'create-outline'
  });
  if (uid && canDeleteComment(gate)) out.push({
    key: 'delete',
    label: 'vibe_btn_delete',
    icon: 'trash-outline',
    destructive: true
  });
  return out;
}
